/**
 * The typed API client.
 *
 * Two conventions it enforces so no call site has to remember them: the bearer token is
 * attached from the current Firebase session, and the active org goes in X-Org-Id. The
 * API rejects any org-scoped request without that header, which is deliberate — a
 * missing org is a 400 rather than a silent read of whichever org happened to be first.
 *
 * Response types are declared here rather than generated. That is a conscious trade for
 * now: `@hono/zod-openapi` would generate them from the server's own schemas, and the
 * plan calls for it, but hand-written types that match the handlers are honest about
 * what the client actually depends on and cost nothing at build time.
 */
import type {
  OrgRole,
  ReminderAudience,
  ReminderFrequency,
  ReminderModule,
  ReminderTrigger,
} from "@onecpa/shared";
import { auth } from "../auth/firebase.js";

declare const __API_BASE_URL__: string;

export interface ApiError extends Error {
  status: number;
  requestId?: string;
  /** The machine-readable code, for the cases the UI must branch on. */
  code?: string;
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; orgId?: string } = {},
): Promise<T> {
  const token = await auth.currentUser?.getIdToken();

  const headers: Record<string, string> = {};
  if (token) headers.authorization = `Bearer ${token}`;
  if (options.orgId) headers["x-org-id"] = options.orgId;
  if (options.body !== undefined) headers["content-type"] = "application/json";

  const response = await fetch(`${__API_BASE_URL__}${path}`, {
    method: options.method ?? "GET",
    headers,
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
  });

  const payload = (await response.json().catch(() => null)) as
    (Record<string, unknown> & { error?: string; message?: string; requestId?: string }) | null;

  if (!response.ok) {
    const error = new Error(
      payload?.message ?? payload?.error ?? `Request failed (${response.status})`,
    ) as ApiError;
    error.status = response.status;
    if (payload?.requestId) error.requestId = payload.requestId;
    if (payload?.error) error.code = payload.error;
    throw error;
  }

  return payload as T;
}

/**
 * Multipart POST.
 *
 * Separate from `request` because the browser has to set the `content-type`
 * itself: a hand-written `multipart/form-data` header omits the boundary, and the
 * server then parses an empty body with no obvious clue why.
 */
async function upload<T>(
  path: string,
  orgId: string,
  fields: Record<string, string | File>,
  fallbackMessage: string,
): Promise<T> {
  const token = await auth.currentUser?.getIdToken();
  const body = new FormData();
  for (const [key, value] of Object.entries(fields)) body.set(key, value);

  const response = await fetch(`${__API_BASE_URL__}${path}`, {
    method: "POST",
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      "x-org-id": orgId,
    },
    body,
  });

  const payload = (await response.json().catch(() => null)) as
    (Record<string, unknown> & { error?: string; message?: string; requestId?: string }) | null;

  if (!response.ok) {
    const error = new Error(payload?.message ?? payload?.error ?? fallbackMessage) as ApiError;
    error.status = response.status;
    if (payload?.error) error.code = payload.error;
    if (payload?.requestId) error.requestId = payload.requestId;
    throw error;
  }

  return payload as T;
}

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  return search.size > 0 ? `?${search.toString()}` : "";
}

// ------------------------------------------------------------------- types

export interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
  role: OrgRole;
  /** Short-lived, minted per request. Null when the firm has no logo. */
  logoUrl: string | null;
  /** The two-letter fallback shown when there is no logo, or it fails to load. */
  monogram: string | null;
}

export interface MeResponse {
  user: { id: string; email: string; fullName: string; mfaEnrolled: boolean };
  organizations: OrgSummary[];
}

export interface ClientRow {
  id: string;
  name: string;
  clientType: "sole_trader" | "company";
  status: "active" | "inactive" | "prospect";
  taxId: string | null;
  email: string | null;
  phoneE164: string | null;
  city: string | null;
  monthlyFee: string | null;
  employsWorkers: boolean;
  assignedStaffUserId: string | null;
  assignedStaffName: string | null;
  internalFileNumber: string | null;
  isDeleted: boolean;
}

export interface ClientListResponse {
  clients: ClientRow[];
  nextCursor: { cursorName: string; cursorId: string } | null;
}

export interface ClientDetail {
  client: ClientRow & {
    incomeTaxFileNumber: string | null;
    vatFileNumber: string | null;
    withholdingFileNumber: string | null;
    addressLine: string | null;
    hourlyCostOverride: string | null;
    /** Files מע״מ מפורט (PCN874) rather than a summary return. */
    vatIsDetailed: boolean;
    notes: string | null;
    createdAt: string;
  };
  frequencies: { authority: string; frequency: string; dueDayOverride: number | null }[];
  compliance: {
    id: string;
    authority: string;
    periodYear: number;
    periodMonth: number;
    status: string;
    dueDate: string;
    penaltyAmount: string | null;
  }[];
  annual: { id: string; taxYear: number; status: string; dueDate: string | null }[];
  wealth: { id: string; declarationDate: string; status: string; dueDate: string | null }[];
  tasks: { id: string; title: string; status: string; priority: string; dueDate: string | null }[];
  files: {
    id: string;
    displayName: string;
    category: string;
    sizeBytes: number;
    scanStatus: string;
    clientVisible: boolean;
    createdAt: string;
  }[];
  money: {
    invoices: {
      id: string;
      number: number | null;
      status: string;
      total: string;
      paidAmount: string;
      dueDate: string | null;
      periodYear: number | null;
      periodMonth: number | null;
    }[];
  } | null;
  profitability:
    | {
        periodYear: number;
        periodMonth: number;
        feeAmount: string | null;
        minutesLogged: number;
        laborCost: string | null;
        marginAmount: string | null;
        band: string;
      }[]
    | null;
}

export interface ImportProblem {
  rowNumber: number;
  column?: string;
  severity: "error" | "warning";
  message: string;
}

export interface ImportRow {
  rowNumber: number;
  name: string;
  clientType: "sole_trader" | "company";
  status: "active" | "inactive" | "prospect";
  email?: string;
  phoneE164?: string;
  addressLine?: string;
  city?: string;
  taxId?: string;
  incomeTaxFileNumber?: string;
  vatFileNumber?: string;
  withholdingFileNumber?: string;
  monthlyFeeAgorot?: number;
  employsWorkers: boolean;
  frequencies: { authority: string; frequency: string }[];
}

export interface ImportPreview {
  rows: ImportRow[];
  problems: ImportProblem[];
  importableCount: number;
  blockedRowNumbers: number[];
  capacity: { maxClients: number; current: number; remaining: number };
  capacityWarning?: string;
}

export interface ClientContact {
  id: string;
  fullName: string;
  role: string | null;
  email: string | null;
  phoneE164: string | null;
  isPrimary: boolean;
  canSign: boolean;
  notes: string | null;
}

export interface ClientTaxFile {
  id: string;
  authority: string;
  fileNumber: string;
  status: string;
  openedOn: string | null;
  closedOn: string | null;
  isPrimary: boolean;
  notes: string | null;
}

export interface ClientNote {
  id: string;
  body: string;
  isPinned: boolean;
  clientVisible: boolean;
  authorName: string | null;
  createdAt: string;
}

export interface ClientSideDetail {
  contacts: ClientContact[];
  taxFiles: ClientTaxFile[];
  notes: ClientNote[];
}

export interface MatrixCell {
  id: string;
  status: string;
  dueDate: string;
  clientConfirmedAt: string | null;
  officeConfirmedAt: string | null;
  clientNote: string | null;
  officeNote: string | null;
  penaltyAmount: string | null;
}

export interface MatrixRow {
  clientId: string;
  clientName: string;
  clientType: string;
  assignedStaffUserId: string | null;
  assignedStaffName: string | null;
  cells: Record<string, MatrixCell | undefined>;
}

export interface MatrixResponse {
  period: { year: number; month: number };
  authorities: string[];
  rows: MatrixRow[];
  summary: Record<string, number>;
}

export interface CaseRow {
  id: string;
  clientId: string;
  clientName: string;
  clientType: string;
  status: string;
  dueDate: string | null;
  officeNote: string | null;
  readyAt: string | null;
  assignedStaffName: string | null;
  itemsTotal: number;
  itemsClosed: number;
  itemsBlocking: number;
  taxYear?: number;
  declarationDate?: string;
}

export interface CaseDetail {
  workspace: CaseRow;
  items: {
    id: string;
    label: string;
    section: string;
    sortOrder: number;
    status: string;
    isRequired: boolean;
    clientVisible: boolean;
    officeNote: string | null;
    clientNote: string | null;
    completedAt: string | null;
  }[];
  files: {
    id: string;
    displayName: string;
    sizeBytes: number;
    scanStatus: string;
    checklistItemId: string | null;
    createdAt: string;
  }[];
}

export interface TaskRow {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  dueDate: string | null;
  recurrence: string | null;
  clientId: string | null;
  clientName: string | null;
  assignedUserId: string | null;
  assignedUserName: string | null;
  sourceTable: string | null;
  completedAt: string | null;
}

export interface TimeEntryRow {
  id: string;
  userId: string;
  userName: string | null;
  clientId: string | null;
  clientName: string | null;
  description: string | null;
  startedAt: string;
  endedAt: string | null;
  minutes: number | null;
  isBillable: boolean;
}

export interface DashboardResponse {
  period: { year: number; month: number };
  role: OrgRole;
  reporting: { total: number; awaiting: number; confirmed: number; late: number; at_risk: number };
  tasks: { open: number; overdue: number; mine: number };
  cases: { blocked: number; ready: number; open: number };
  money: { outstanding: string; overdue_count: number } | null;
  minutesThisMonth: number;
  upcoming: {
    id: string;
    title: string;
    dueDate: string;
    kind: string;
    clientId: string | null;
    estimatedHours: number | null;
  }[];
  myTasks: {
    id: string;
    title: string;
    dueDate: string | null;
    priority: string;
    clientName: string | null;
  }[];
}

export type ReminderChannel = "in_app" | "email" | "whatsapp" | "sms";

export interface ReminderRuleInput {
  title: string;
  module: ReminderModule;
  audience: ReminderAudience;
  trigger: ReminderTrigger;
  statusTrigger?: string | null;
  offsetDays: number;
  intervalDays?: number | null;
  frequency: ReminderFrequency;
  channels: ReminderChannel[];
  messageTemplateKey: string;
  clientId?: string | null;
  isActive?: boolean;
}

export interface ReminderRuleRow extends ReminderRuleInput {
  id: string;
  statusTrigger: string | null;
  intervalDays: number | null;
  clientId: string | null;
  clientName: string | null;
  isActive: boolean;
  /** One of the four rungs the product ships with; disabled rather than deleted. */
  isSystemDefault: boolean;
}

/** Mirrors `AuthorityReportParse` in @onecpa/domain. */
export interface AuthorityParse {
  authority: string;
  period: { year: number; month: number } | null;
  isBiMonthly: boolean;
  amountToPay: number;
  summary: Record<string, unknown>;
  errors: string[];
  warnings: string[];
}

export interface AuthorityFilePreview {
  detected: "vat" | "vat_detailed";
  fileName: string;
  sizeBytes: number;
  parse: AuthorityParse;
  /** Matched on the עוסק number in the file, so the usual case is a confirmation. */
  suggestedClient: { id: string; name: string } | null;
  compId: string | null;
}

export interface InvoiceRow {
  id: string;
  number: number | null;
  clientId: string;
  clientName: string;
  status: string;
  issueDate: string | null;
  dueDate: string | null;
  periodYear: number | null;
  periodMonth: number | null;
  subtotal: string;
  vatAmount: string;
  total: string;
  paidAmount: string;
}

export interface AgingResponse {
  buckets: Record<string, { count: number; amountAgorot: number }>;
  totalOutstandingAgorot: number;
  invoices: (InvoiceRow & { daysOverdue: number; outstandingAgorot: number; bucket: string })[];
}

export interface FileRow {
  id: string;
  clientId: string | null;
  clientName: string | null;
  category: string;
  displayName: string;
  mimeType: string;
  sizeBytes: number;
  scanStatus: string;
  scanDetail: string | null;
  clientVisible: boolean;
  uploadedByName: string | null;
  createdAt: string;
}

export interface PortalResponse {
  client: { id: string; name: string; clientType: string };
  period: { year: number; month: number };
  headline: { text: string; tone: "success" | "pending" | "warning" };
  obligations: {
    id: string;
    authority: string;
    authorityLabel: string;
    status: string;
    dueDate: string;
    clientConfirmedAt: string | null;
    officeConfirmedAt: string | null;
    officeNote: string | null;
  }[];
  requests: {
    id: string;
    label: string;
    section: string;
    status: string;
    officeNote: string | null;
    workspaceKind: string;
    workspaceId: string;
  }[];
  documents: {
    id: string;
    displayName: string;
    sizeBytes: number;
    scanStatus: string;
    createdAt: string;
  }[];
  signatures: { id: string; title: string; status: string; expiresAt: string | null }[];
  canConfirm: boolean;
}

// ------------------------------------------------------------------ client

export const api = {
  me: () => request<MeResponse>("/v1/me"),

  signup: (body: { firmName: string; plan: "solo" | "office"; fullName?: string }) =>
    request<{ organization: { orgId: string; slug: string; trialEndsAt: string } }>(
      "/v1/onboarding/signup",
      { method: "POST", body },
    ),

  acceptInvitation: (body: { token: string; fullName?: string }) =>
    request<{ membership: { orgId: string; role: OrgRole } }>("/v1/onboarding/accept-invitation", {
      method: "POST",
      body,
    }),

  clients: {
    list: (
      orgId: string,
      params: {
        search?: string;
        status?: string;
        assignedTo?: string;
        includeDeleted?: boolean;
        limit?: number;
        cursorName?: string;
        cursorId?: string;
      } = {},
    ) => request<ClientListResponse>(`/v1/clients${query(params)}`, { orgId }),

    get: (orgId: string, id: string) => request<ClientDetail>(`/v1/clients/${id}`, { orgId }),

    create: (orgId: string, body: Record<string, unknown>) =>
      request<{ client: { id: string; name: string } }>("/v1/clients", {
        method: "POST",
        body,
        orgId,
      }),

    update: (orgId: string, id: string, body: Record<string, unknown>) =>
      request<{ client: { id: string; name: string } }>(`/v1/clients/${id}`, {
        method: "PATCH",
        body,
        orgId,
      }),

    remove: (orgId: string, id: string) =>
      request<{ client: { id: string } }>(`/v1/clients/${id}`, { method: "DELETE", orgId }),

    restore: (orgId: string, id: string) =>
      request<{ client: { id: string } }>(`/v1/clients/${id}/restore`, { method: "POST", orgId }),

    /**
     * Parse and report. Multipart rather than JSON because the browser has a File, and
     * base64ing a 5MB workbook through JSON would triple it for no benefit.
     */
    /** The card's people, tax files and notes, in one request. */
    detail: (orgId: string, id: string) =>
      request<ClientSideDetail>(`/v1/clients/${id}/detail`, { orgId }),

    addContact: (orgId: string, id: string, body: Record<string, unknown>) =>
      request<{ contact: { id: string } }>(`/v1/clients/${id}/contacts`, {
        method: "POST",
        body,
        orgId,
      }),

    updateContact: (orgId: string, id: string, contactId: string, body: Record<string, unknown>) =>
      request<{ contact: { id: string } }>(`/v1/clients/${id}/contacts/${contactId}`, {
        method: "PATCH",
        body,
        orgId,
      }),

    removeContact: (orgId: string, id: string, contactId: string) =>
      request<{ removed: string }>(`/v1/clients/${id}/contacts/${contactId}`, {
        method: "DELETE",
        orgId,
      }),

    addTaxFile: (orgId: string, id: string, body: Record<string, unknown>) =>
      request<{ taxFile: { id: string } }>(`/v1/clients/${id}/tax-files`, {
        method: "POST",
        body,
        orgId,
      }),

    addNote: (orgId: string, id: string, body: Record<string, unknown>) =>
      request<{ note: { id: string } }>(`/v1/clients/${id}/notes`, {
        method: "POST",
        body,
        orgId,
      }),

    updateNote: (orgId: string, id: string, noteId: string, body: Record<string, unknown>) =>
      request<{ note: { id: string } }>(`/v1/clients/${id}/notes/${noteId}`, {
        method: "PATCH",
        body,
        orgId,
      }),

    importPreview: async (orgId: string, file: File) => {
      const token = await auth.currentUser?.getIdToken();
      const body = new FormData();
      body.set("file", file);

      const response = await fetch(`${__API_BASE_URL__}/v1/clients/import/preview`, {
        method: "POST",
        // No content-type: the browser must set the multipart boundary itself.
        headers: {
          ...(token ? { authorization: `Bearer ${token}` } : {}),
          "x-org-id": orgId,
        },
        body,
      });

      const payload = (await response.json().catch(() => null)) as
        (ImportPreview & { error?: string; message?: string }) | null;

      if (!response.ok) {
        const error = new Error(payload?.message ?? payload?.error ?? "הייבוא נכשל") as ApiError;
        error.status = response.status;
        throw error;
      }

      return payload as ImportPreview;
    },

    importCommit: (orgId: string, rows: ImportRow[]) =>
      request<{
        created: number;
        skipped: { rowNumber: number; reason: string }[];
        clients: { rowNumber: number; id: string; name: string }[];
      }>("/v1/clients/import/commit", { method: "POST", body: { rows }, orgId }),

    /** Both downloads stream a workbook, so they bypass the JSON wrapper entirely. */
    importTemplate: (orgId: string) => downloadWorkbook(orgId, "/v1/clients/import/template"),
    importExport: (orgId: string) => downloadWorkbook(orgId, "/v1/clients/import/export"),

    setFrequencies: (
      orgId: string,
      id: string,
      frequencies: { authority: string; frequency: string; dueDayOverride?: number | null }[],
    ) =>
      request<{ updated: number }>(`/v1/clients/${id}/frequencies`, {
        method: "PUT",
        body: { frequencies },
        orgId,
      }),
  },

  reporting: {
    matrix: (orgId: string, year: number, month: number) =>
      request<MatrixResponse>(`/v1/reporting/matrix${query({ year, month })}`, { orgId }),

    exceptions: (orgId: string) =>
      request<{
        exceptions: {
          id: string;
          clientId: string;
          clientName: string;
          authority: string;
          periodYear: number;
          periodMonth: number;
          status: string;
          dueDate: string;
          penaltyAmount: string | null;
          assignedStaffName: string | null;
          risk: string;
        }[];
      }>("/v1/reporting/exceptions", { orgId }),

    confirmOffice: (orgId: string, id: string, note?: string) =>
      request<{ row: { id: string; status: string } }>(`/v1/reporting/${id}/confirm-office`, {
        method: "POST",
        body: { ...(note ? { note } : {}) },
        orgId,
      }),

    recordPenalty: (orgId: string, id: string, amount: number, note?: string) =>
      request<{ row: { id: string } }>(`/v1/reporting/${id}/penalty`, {
        method: "POST",
        body: { amount, ...(note ? { note } : {}) },
        orgId,
      }),

    generate: (orgId: string, year: number, month: number) =>
      request<{ created: number }>("/v1/reporting/generate", {
        method: "POST",
        body: { year, month },
        orgId,
      }),

    authorityReports: (orgId: string, params: { year?: number; month?: number } = {}) =>
      request<{
        reports: {
          id: string;
          clientId: string;
          clientName: string;
          authority: string;
          periodYear: number;
          periodMonth: number;
          status: string;
          validationSummary: string | null;
          validationErrorCount: number;
          transmittedAt: string | null;
          confirmationNumber: string | null;
          isBiMonthly: boolean;
          amountToPay: string | null;
          sourceFileName: string | null;
        }[];
      }>(`/v1/reporting/authority-reports${query(params)}`, { orgId }),

    transmit: (orgId: string, id: string) =>
      request<{ report: { id: string; status: string; confirmationNumber: string | null } }>(
        `/v1/reporting/authority-reports/${id}/transmit`,
        { method: "POST", orgId },
      ),

    /** Parses the file and returns what it says. Writes nothing. */
    previewAuthorityFile: (orgId: string, file: File) =>
      upload<AuthorityFilePreview>(
        "/v1/reporting/authority-reports/preview",
        orgId,
        { file },
        "לא ניתן לקרוא את הקובץ",
      ),

    /** Stores the file and records the filing. The server parses it again. */
    ingestAuthorityFile: (
      orgId: string,
      input: { file: File; clientId: string; deriveAdvance?: boolean; advanceAmount?: number },
    ) =>
      upload<{
        report: { id: string; status: string } | null;
        advance: { id: string; status: string } | null;
        parse: AuthorityParse;
      }>(
        "/v1/reporting/authority-reports",
        orgId,
        {
          file: input.file,
          clientId: input.clientId,
          ...(input.deriveAdvance ? { deriveAdvance: "true" } : {}),
          ...(input.advanceAmount !== undefined
            ? { advanceAmount: String(input.advanceAmount) }
            : {}),
        },
        "העלאת הקובץ נכשלה",
      ),
  },

  cases: {
    list: (orgId: string, kind: "annual" | "wealth") =>
      request<{ kind: string; cases: CaseRow[]; summary: Record<string, number> }>(
        `/v1/cases/${kind}`,
        { orgId },
      ),

    get: (orgId: string, kind: "annual" | "wealth", id: string) =>
      request<CaseDetail>(`/v1/cases/${kind}/${id}`, { orgId }),

    open: (orgId: string, kind: "annual" | "wealth", body: Record<string, unknown>) =>
      request<{ case: { id: string; itemCount: number } }>(`/v1/cases/${kind}`, {
        method: "POST",
        body,
        orgId,
      }),

    updateItem: (
      orgId: string,
      kind: "annual" | "wealth",
      caseId: string,
      itemId: string,
      body: Record<string, unknown>,
    ) =>
      request<{
        item: { id: string; status: string };
        progress: { total: number; closed: number; status: string };
      }>(`/v1/cases/${kind}/${caseId}/items/${itemId}`, { method: "PATCH", body, orgId }),
  },

  tasks: {
    list: (orgId: string, params: { status?: string; mine?: boolean; clientId?: string } = {}) =>
      request<{ tasks: TaskRow[] }>(`/v1/tasks${query(params)}`, { orgId }),

    create: (orgId: string, body: Record<string, unknown>) =>
      request<{ task: { id: string; title: string } }>("/v1/tasks", {
        method: "POST",
        body,
        orgId,
      }),

    update: (orgId: string, id: string, body: Record<string, unknown>) =>
      request<{ task: { id: string; status: string } }>(`/v1/tasks/${id}`, {
        method: "PATCH",
        body,
        orgId,
      }),
  },

  time: {
    list: (orgId: string, params: { clientId?: string; from?: string; to?: string } = {}) =>
      request<{ entries: TimeEntryRow[]; totalMinutes: number }>(`/v1/time${query(params)}`, {
        orgId,
      }),

    running: (orgId: string) =>
      request<{
        running: {
          id: string;
          clientId: string | null;
          clientName: string | null;
          description: string | null;
          startedAt: string;
        } | null;
      }>("/v1/time/running", { orgId }),

    start: (orgId: string, body: { clientId?: string | null; description?: string }) =>
      request<{ running: { id: string; startedAt: string } }>("/v1/time/start", {
        method: "POST",
        body,
        orgId,
      }),

    stop: (orgId: string) =>
      request<{ entry: { id: string; minutes: number } }>("/v1/time/stop", {
        method: "POST",
        orgId,
      }),

    add: (orgId: string, body: Record<string, unknown>) =>
      request<{ entry: { id: string } }>("/v1/time", { method: "POST", body, orgId }),
  },

  documents: {
    list: (
      orgId: string,
      params: { clientId?: string; category?: string; search?: string; scanStatus?: string } = {},
    ) =>
      request<{ files: FileRow[]; nextCursor: unknown }>(`/v1/documents${query(params)}`, {
        orgId,
      }),

    uploadUrl: (orgId: string, body: Record<string, unknown>) =>
      request<{
        fileId: string;
        upload: { url: string; method: string; headers: Record<string, string>; expiresAt: string };
      }>("/v1/documents/upload-url", { method: "POST", body, orgId }),

    complete: (orgId: string, id: string) =>
      request<{ file: { id: string } | null; scanQueued: boolean }>(
        `/v1/documents/${id}/complete`,
        {
          method: "POST",
          orgId,
        },
      ),

    download: (orgId: string, id: string) =>
      request<{ url: string; expiresAt: string }>(`/v1/documents/${id}/download`, { orgId }),

    update: (orgId: string, id: string, body: Record<string, unknown>) =>
      request<{ file: { id: string; clientVisible: boolean } }>(`/v1/documents/${id}`, {
        method: "PATCH",
        body,
        orgId,
      }),
  },

  billing: {
    invoices: (orgId: string, params: { status?: string; clientId?: string } = {}) =>
      request<{ invoices: InvoiceRow[] }>(`/v1/billing/invoices${query(params)}`, { orgId }),

    aging: (orgId: string) => request<AgingResponse>("/v1/billing/aging", { orgId }),

    generate: (orgId: string, year: number, month: number) =>
      request<{ created: number }>("/v1/billing/generate", {
        method: "POST",
        body: { year, month },
        orgId,
      }),

    issue: (orgId: string, id: string) =>
      request<{ invoice: { id: string; number: number | null } }>(
        `/v1/billing/invoices/${id}/issue`,
        { method: "POST", orgId },
      ),

    recordPayment: (
      orgId: string,
      id: string,
      body: { amount: number; method?: string; receivedOn: string; reference?: string },
    ) =>
      request<{ invoice: { id: string; status: string }; settled: boolean }>(
        `/v1/billing/invoices/${id}/payments`,
        { method: "POST", body, orgId },
      ),

    writeOff: (orgId: string, id: string, reason: string) =>
      request<{ invoice: { id: string } }>(`/v1/billing/invoices/${id}/write-off`, {
        method: "POST",
        body: { reason },
        orgId,
      }),

    feeSchedules: (orgId: string) =>
      request<{
        schedules: {
          id: string;
          clientId: string;
          clientName: string;
          label: string;
          amount: string;
          intervalMonths: number;
          startsOn: string;
          endsOn: string | null;
          isActive: boolean;
        }[];
      }>("/v1/billing/fee-schedules", { orgId }),
  },

  analytics: {
    dashboard: (orgId: string) => request<DashboardResponse>("/v1/analytics/dashboard", { orgId }),

    profitability: (orgId: string, params: { year?: number; month?: number } = {}) =>
      request<{
        period: { year: number; month: number };
        rows: {
          clientId: string;
          clientName: string;
          feeAmount: string | null;
          minutesLogged: number;
          laborCost: string | null;
          marginAmount: string | null;
          band: string;
          computedAt: string;
        }[];
        totals: { feeAgorot: number; laborAgorot: number; marginAgorot: number; minutes: number };
        bands: Record<string, number>;
      }>(`/v1/analytics/profitability${query(params)}`, { orgId }),

    engagement: (orgId: string, params: { year?: number; month?: number } = {}) =>
      request<{
        rows: {
          clientId: string;
          clientName: string;
          score: number;
          level: string;
          onTimeCount: number;
          lateCount: number;
          remindersSent: number;
          averageResponseDays: number | null;
        }[];
      }>(`/v1/analytics/engagement${query(params)}`, { orgId }),

    workload: (orgId: string) =>
      request<{
        workload: {
          user_id: string;
          full_name: string;
          client_count: number;
          open_tasks: number;
          minutes_this_month: number;
          blocked_cases: number;
        }[];
      }>("/v1/analytics/workload", { orgId }),

    calendar: (orgId: string, params: { from?: string; to?: string } = {}) =>
      request<{
        range: { from: string; to: string };
        entries: {
          id: string;
          clientId: string | null;
          clientName: string | null;
          kind: string;
          title: string;
          dueDate: string;
          estimatedHours: number | null;
          assignedStaffName: string | null;
          completedAt: string | null;
        }[];
        workloadByWeek: { week: string; entries: number; hours: number }[];
      }>(`/v1/analytics/calendar${query(params)}`, { orgId }),

    audit: (orgId: string) =>
      request<{
        events: {
          id: string;
          action: string;
          entityType: string;
          entityId: string | null;
          actorEmail: string | null;
          ip: string | null;
          requestId: string | null;
          createdAt: string;
        }[];
      }>("/v1/analytics/audit", { orgId }),
  },

  settings: {
    members: (orgId: string) =>
      request<{
        members: {
          id: string;
          userId: string;
          role: OrgRole;
          clientId: string | null;
          fullName: string;
          email: string;
          phoneE164: string | null;
          mfaEnrolled: boolean;
          lastSeenAt: string | null;
          joinedAt: string;
        }[];
      }>("/v1/settings/members", { orgId }),

    updateMember: (orgId: string, id: string, role: OrgRole) =>
      request<{ member: { id: string; role: string } }>(`/v1/settings/members/${id}`, {
        method: "PATCH",
        body: { role },
        orgId,
      }),

    removeMember: (orgId: string, id: string) =>
      request<{ removed: string }>(`/v1/settings/members/${id}`, { method: "DELETE", orgId }),

    invitations: (orgId: string) =>
      request<{
        invitations: {
          id: string;
          email: string;
          role: OrgRole;
          expiresAt: string;
          state: string;
          createdAt: string;
        }[];
      }>("/v1/settings/invitations", { orgId }),

    invite: (orgId: string, body: { email: string; role: OrgRole; clientId?: string }) =>
      request<{ invitation: { id: string; email: string }; token: string; expiresAt: string }>(
        "/v1/settings/invitations",
        { method: "POST", body, orgId },
      ),

    revokeInvitation: (orgId: string, id: string) =>
      request<{ invitation: { id: string } }>(`/v1/settings/invitations/${id}/revoke`, {
        method: "POST",
        orgId,
      }),

    templates: (orgId: string) =>
      request<{
        templates: {
          id: string;
          key: string;
          channel: string;
          subject: string | null;
          body: string;
          isActive: boolean;
          description: string | null;
          isModified: boolean;
          updatedAt: string;
        }[];
        availableKeys: { key: string; description: string }[];
      }>("/v1/settings/message-templates", { orgId }),

    updateTemplate: (orgId: string, id: string, body: Record<string, unknown>) =>
      request<{ template: { id: string } }>(`/v1/settings/message-templates/${id}`, {
        method: "PATCH",
        body,
        orgId,
      }),

    restoreTemplate: (orgId: string, id: string) =>
      request<{ template: { id: string } }>(`/v1/settings/message-templates/${id}/restore`, {
        method: "POST",
        orgId,
      }),

    reminderRules: (orgId: string) =>
      request<{ rules: ReminderRuleRow[] }>("/v1/settings/reminder-rules", { orgId }),

    createReminderRule: (orgId: string, body: ReminderRuleInput) =>
      request<{ rule: { id: string } }>("/v1/settings/reminder-rules", {
        method: "POST",
        body,
        orgId,
      }),

    updateReminderRule: (orgId: string, id: string, body: Partial<ReminderRuleInput>) =>
      request<{ rule: { id: string } }>(`/v1/settings/reminder-rules/${id}`, {
        method: "PATCH",
        body,
        orgId,
      }),

    deleteReminderRule: (orgId: string, id: string) =>
      request<{ id: string; deactivated: boolean }>(`/v1/settings/reminder-rules/${id}`, {
        method: "DELETE",
        orgId,
      }),

    restoreReminderDefaults: (orgId: string) =>
      request<{ restored: number }>("/v1/settings/reminder-rules/restore-defaults", {
        method: "POST",
        orgId,
      }),

    messages: (orgId: string) =>
      request<{
        sent: {
          id: string;
          clientName: string | null;
          channel: string;
          templateKey: string;
          recipientAddress: string | null;
          renderedBody: string | null;
          deliveryStatus: string;
          sentAt: string;
          readAt: string | null;
        }[];
        queued: {
          id: string;
          clientName: string | null;
          channel: string;
          templateKey: string;
          status: string;
          scheduledFor: string;
          attempts: number;
          lastError: string | null;
        }[];
      }>("/v1/settings/messages", { orgId }),

    organization: (orgId: string) =>
      request<{
        org: {
          id: string;
          name: string;
          slug: string;
          plan: string;
          status: string;
          trialEndsAt: string | null;
          maxClients: number;
          maxSeats: number | null;
          portalEnabled: boolean;
        };
        settings: { key: string; value: Record<string, unknown> }[];
        usage: { clients: number; seats: number };
        onboarding: { step: string; completedAt: string | null }[];
      }>("/v1/settings/organization", { orgId }),

    updateOrganization: (orgId: string, body: Record<string, unknown>) =>
      request<{ organization: { id: string; name: string } }>("/v1/settings/organization", {
        method: "PATCH",
        body,
        orgId,
      }),

    putSetting: (orgId: string, key: string, value: Record<string, unknown>) =>
      request<{ setting: { key: string } }>(`/v1/settings/settings/${key}`, {
        method: "PUT",
        body: { value },
        orgId,
      }),
  },

  /**
   * Development-only endpoints.
   *
   * Reachable only when the API was started with ENABLE_DEV_ENDPOINTS, and the screen that calls
   * them is behind the same compile-time flag as the gallery — so these are absent from a
   * production build rather than merely unused.
   */
  dev: {
    clock: () =>
      request<{
        pinned: boolean;
        offsetMs: number;
        offsetDays: number;
        now: string;
        realNow: string;
      }>("/__dev/clock"),

    setClock: (body: { days?: number; hours?: number; reset?: boolean; pinTo?: string }) =>
      request<{
        pinned: boolean;
        offsetMs: number;
        offsetDays: number;
        now: string;
        realNow: string;
      }>("/__dev/clock", { method: "POST", body }),

    outbox: () =>
      request<{
        counts: Record<string, number>;
        messages: {
          messageId: string;
          channel: string;
          templateKey: string;
          sentAt: string;
          renderedBody: string;
          recipient: { email?: string; displayName?: string };
        }[];
      }>("/__dev/outbox"),

    messages: (orgId: string) =>
      request<{
        queued: {
          id: string;
          templateKey: string;
          channel: string;
          status: string;
          recipientAddress: string | null;
          scheduledFor: string;
          attempts: number;
          lastError: string | null;
          idempotencyKey: string;
        }[];
        sent: {
          id: string;
          templateKey: string;
          channel: string;
          renderedBody: string | null;
          deliveryStatus: string;
          sentAt: string;
          clientName: string | null;
        }[];
      }>("/__dev/messages", { orgId }),

    escalationLadder: () =>
      request<{
        rungs: {
          afterDays: number;
          trigger: string;
          action: string;
          audience: string;
          message: string;
          tone: string;
        }[];
      }>("/__dev/escalation-ladder"),

    renderTemplate: (key: string, variables: Record<string, string>) =>
      request<{
        key: string;
        channel: string;
        subject: string | null;
        body: string;
        unfilled: string[];
        whatsappPayload: unknown;
      }>("/__dev/render-template", { method: "POST", body: { key, variables } }),
  },

  portal: {
    me: (orgId: string) => request<PortalResponse>("/v1/portal/me", { orgId }),

    confirmMonth: (orgId: string, note?: string) =>
      request<{ confirmed: number; period: { year: number; month: number } }>(
        "/v1/portal/confirm-month",
        { method: "POST", body: { ...(note ? { note } : {}) }, orgId },
      ),

    history: (orgId: string) =>
      request<{
        history: {
          id: string;
          authority: string;
          authorityLabel: string;
          periodYear: number;
          periodMonth: number;
          status: string;
          dueDate: string;
          clientConfirmedAt: string | null;
          officeConfirmedAt: string | null;
          penaltyAmount: string | null;
        }[];
      }>("/v1/portal/history", { orgId }),
  },
};

/**
 * Downloads a workbook.
 *
 * Fetched with the bearer token rather than opened in a new tab, because the API authenticates
 * on the Authorization header and a plain link carries none. The object URL is revoked
 * immediately after the click — leaving it alive keeps the whole file in memory.
 */
async function downloadWorkbook(orgId: string, path: string): Promise<void> {
  const token = await auth.currentUser?.getIdToken();
  const response = await fetch(`${__API_BASE_URL__}${path}`, {
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      "x-org-id": orgId,
    },
  });

  if (!response.ok) throw new Error(`Download failed (${response.status})`);

  const blob = await response.blob();
  const fileName =
    /filename="([^"]+)"/.exec(response.headers.get("content-disposition") ?? "")?.[1] ??
    "onecpa.xlsx";

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

/** Money crosses the wire as a decimal string, deliberately. Convert at the edge. */
export function toAgorot(value: string | null | undefined): number {
  return value === null || value === undefined ? 0 : Math.round(Number(value) * 100);
}
